import { Box, useTheme } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import { PaymentModule } from '@sm-dashboard/types';
import { CONTACT_US_NUMBER } from '@/constants';
import { Button } from '@sm-dashboard/ui-components';

const PAID_GATE_COPY: Record<string, { tagline: string; title: string; paragraphs: [string, string] }> = {
  'קדמה דפים אישיים': {
    tagline: 'על דברים טובים משלמים…',
    title: 'דפים אישיים',
    paragraphs: [
      'המנויים שלנו נהנים מקטלוג מתפתח של דפי נחיתה מעוצבים שעושים את העבודה ומביאים תוצאות.',
      'מגוון דפים עם מערכת ניהול ועיצוב, להתרמות, הרשמות, מכירת כרטיסים ואיסוף לידים - והכול עם חיבור לסליקת האשראי שלך!'
    ]
  },
  תרומות: {
    tagline: 'על דברים טובים משלמים…',
    title: 'תרומות',
    paragraphs: [
      'מסך התרומות זמין למנויים.',
      'כאן עוקבים אחרי התרומות שנכנסו, הזיכויים והפעילות בסניף.'
    ]
  },
  CRM: {
    tagline: 'על דברים טובים משלמים…',
    title: 'CRM',
    paragraphs: [
      'המנויים שלנו נהנים ממערכת לניהול קשרי קהילה ומקורבים.',
      'הצטרף גם אתה ותתחיל ליהנות מארגז כלים דיגיטליים שיעזרו לך לבנות קהילה להצלחה.'
    ]
  }
};

const STATUS_CONFIG = {
  inDevelopment: {
    title: 'במהלך פיתוח',
    description: 'המודול נמצא בשלבי פיתוח מתקדמים. אנו עובדים על הוספת תכונות חדשות ושיפור הביצועים.',
    color: 'warning.main',
    icon: '🔧',
    details: 'צפי להשקה: בעוד מספר שבועות',
    contactAction: false
  },
  pilot: {
    title: 'במהלך פיילוט',
    description: 'המודול זמין למשתמשים נבחרים לבדיקה וקבלת משוב. אנו אוספים נתונים לשיפור השירות.',
    color: 'info.main',
    icon: '🧪',
    details: 'להצטרפות לפיילוט -',
    contactAction: true
  },
  inactive: {
    title: 'לא פעיל',
    description: 'המודול זמנית לא זמין לשימוש. אנו עובדים על פתרון הבעיות ועדכון המערכת.',
    color: 'error.main',
    icon: '⏸️',
    details: 'סטטוס: בתחזוקה',
    contactAction: false
  }
} as const;

const PaymentRequiredDialog = ({ modules }: { modules: PaymentModule[] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { name, description, status, tag } = modules[0];

  const handleSubscribe = () => {
    navigate('/org/subscription-plans');
  };

  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || {
    title: 'מודל בתשלום - יש להסדיר הרשמה',
    description: 'אין לך הרשאות גישה למודל זה',
    color: 'warning.main',
    icon: '⚠️',
    details: 'סטטוס: בתשלום',
    contactAction: true
  };

  const gateCopy = !status ? PAID_GATE_COPY[tag] : undefined;
  const headerTitle = gateCopy?.tagline ?? config.title;
  const moduleTitle = gateCopy?.title ?? name;
  const bodyParagraphs = gateCopy?.paragraphs ?? [description];

  return (
    <Box sx={{ maxWidth: 'md', width: '100%', p: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          mt: { xs: 5, md: 0 },
          mb: { xs: 5, md: 0 },
          p: { xs: 1, md: 6 },
          borderRadius: { xs: 1, md: 4 },
          bgcolor: `${config.color}15`,
          border: `2px solid ${theme.palette[config.color as keyof typeof theme.palette] || config.color}30`,
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, md: 2 },
            mb: { xs: 2, md: 3 }
          }}
        >
          {!gateCopy ? (
            <Box sx={{ fontSize: { xs: '2rem', md: '3rem' } }} color={config.color}>
              {config.icon}
            </Box>
          ) : null}
          <Box sx={{ fontSize: { xs: '1.25rem', md: '2rem' }, fontWeight: 'bold' }} color={config.color}>
            {headerTitle}
          </Box>
        </Box>

        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Box sx={{ fontSize: { xs: '1.1rem', md: '1.5rem' }, fontWeight: 'bold', mb: 1 }} color="text.primary">
            {moduleTitle}
          </Box>
          {bodyParagraphs.map((paragraph, index) => (
            <Box
              key={index}
              color="text.secondary"
              sx={{
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '0.85rem', md: '1rem' },
                mt: index > 0 ? 1.5 : 0
              }}
            >
              {paragraph}
            </Box>
          ))}
        </Box>

        {!gateCopy ? (
          <>
            <Box
              color="text.secondary"
              sx={{ mb: { xs: 2, md: 3 }, fontWeight: 'medium', fontSize: { xs: '0.85rem', md: '1rem' } }}
            >
              {config.description}
            </Box>

            <Box
              sx={{
                display: 'inline-block',
                px: { xs: 2, md: 3 },
                py: 1,
                mb: { xs: 3, md: 4 },
                borderRadius: 2,
                bgcolor: `${config.color}20`,
                border: `1px solid ${theme.palette[config.color as keyof typeof theme.palette] || config.color}40`
              }}
            >
              <Box color={config.color} sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                {config.details}
              </Box>
            </Box>
          </>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 }
          }}
        >
          <Button
            size="large"
            startIcon={<ShoppingCartIcon />}
            onClick={handleSubscribe}
            disabled={status === 'inDevelopment' || status === 'inactive'}
          >
            {gateCopy ? 'לרכישת מנוי' : 'הרשמה עכשיו'}
          </Button>

          {config.contactAction && (
            <Button
              color="success"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                const whatsappUrl = `https://wa.me/${CONTACT_US_NUMBER}`;
                window.open(whatsappUrl, '_blank');
              }}
            >
              {gateCopy ? 'לעוד פרטים' : 'צור קשר בווטסאפ'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentRequiredDialog;
